<template>
    <div class="relative overflow-hidden bg-white p-16 rounded shadow">
        <template v-if="loading">
            <h2 class="mt-64 text-heading-2">{{ capitalize($t('calendar.loading')) }}</h2>
            <loading></loading>
        </template>

        <template v-else-if="hasCalendar">
            <div class="w-1/2 2xl:w-full dashboard-calendar">
                <v-date-picker
                    @dayclick="changeDatePicker"
                    @update:from-page="changeMonth"
                    :modelValue="activeDate"
                    :attributes="calendarEvents"
                    :select-attribute="selectAttribute"
                    trim-weeks
                    is-expanded
                    is-required
                    transition="fade"
                    title-position="left"
                    :min-page="minPage"
                    :max-page="maxPage"
                />
                <div class="py-10 flex gap-10">
                    <div v-for="item in legend" class="flex items-center">
                        <div class="w-12 h-12 rounded-32" :class="item.color"></div>
                        <p class="inline-block ml-4">{{ capitalize(item.label) }}</p>
                    </div>
                </div>
            </div>
            <div class="border-r-2 border-primary-20 mx-32 2xl:hidden"></div>
            <div
                class="w-1/2 2xl:w-full overflow-y-scroll scroll-styled flex flex-col"
                :class="loadingEvents ? 'flex flex-col justify-center items-center h-full' : ''"
            >
                <h2
                    class="text-body-sm font-semibold"
                    :class="loadingEvents ? 'hidden' : 'block'"
                >
                    {{ agendaLabel }}
                </h2>
                <div class="border-t border-primary-20 mt-12"></div>
                <div class="h-full overflow-y-scroll overflow-container snap-y hide-scrollbar pt-12">
                    <div v-if="!loadingEvents" class="space-y-12">
                        <div v-if="selectedDayTasks.length">
                            <div class="flex justify-between w-full" :class="showTasks ? 'mb-12' : ''">
                                <div class="flex gap-8 items-center">
                                    <svg-icon class="w-16 h-16" type="task-alt" />
                                    <h3 class="text-body-sm font-semibold">{{ $t('tasks.tasks') }}</h3>
                                </div>
                                <button class="cursor-pointer transition-transform ease-in-out duration-200"
                                        @click="showTasks = !showTasks">
                                    <svg-icon
                                        type="keyboard-arrow-down"
                                        :class="showTasks ? 'rotate-180' : ''"
                                        class="w-18 h-18"
                                    />
                                </button>
                            </div>
                            <div
                                class="w-full overflow-y-hidden space-y-10"
                                :class="showTasks ? 'h-full' : 'h-0'"
                            >
                                <div v-for="calendarEvent in selectedDayTasks" :key="calendarEvent.id"
                                     class="border-l-4 border-semantic-success bg-primary-5 flex items-center justify-between rounded-6 w-full px-12 py-8">
                                    <div class="flex items-center gap-5">
                                        <svg-icon v-if="calendarEvent.fulfilled" type="check-circle" width="16"
                                                  height="16"
                                                  class="text-secondary"/>
                                        <p class="event-title font-semibold m-0">
                                            {{ calendarEvent.title }}
                                        </p>
                                    </div>
                                    <p v-if="calendarEvent.fulfilled" class="text-primary-80 font-semibold text-body-sm">
                                        {{ moment(calendarEvent.fulfilled) }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div v-if="selectedDayTasks.length" class="border-b border-primary-20"></div>
                        <div v-for="(calendarEvent, index) in selectedDayAllDayEvents" :key="calendarEvent.id"
                             class="flex justify-between border-l-4 px-12 py-8 rounded-6 bg-primary-5 cursor-pointer"
                             :style="'border-color:' + (calendarEvent.participation_status === 'needs_action' ? styles.colors.semantic.error.DEFAULT : styles.colors.semantic.warning.DEFAULT) + ';'"
                             @click="eventSelected(calendarEvent)"
                        >
                            <div class="flex items-center space-x-8">
                                <svg-icon class="w-18 h-18" type="business-center"></svg-icon>
                                <p class="event-title font-semibold m-0">{{ calendarEvent.title }}</p>
                                <p class="m-0">{{ capitalize($t('calendar.dashboard.all_day')) }}</p>
                            </div>
                            <div class="flex items-center space-x-2">
                                <p class="m-0">
                                    {{ calendarEvent.transparency === "opaque" ? 'busy' : (calendarEvent.transparency === "transparent" ? 'free' : '') }}
                                </p>
                                <svg-icon
                                    :type="calendarEvent.attendees.length < 2 ? 'person' : 'groups'"
                                    class="w-18 h-18"
                                />
                            </div>
                        </div>
                        <div v-if="selectedDayAllDayEvents.length" class="border-b border-primary-20"></div>
                        <div v-for="calendarEvent in selectedDayEvents" :key="calendarEvent.id"
                             class="flex justify-between border-l-4 px-12 py-8 rounded-6 bg-primary-5 cursor-pointer"
                             :style="'border-color:' + (calendarEvent.participation_status === 'needs_action' ? styles.colors.semantic.warning.DEFAULT : styles.colors.primary[80]) + ';'"
                             @click="eventSelected(calendarEvent)"
                        >
                            <div class="flex items-center space-x-8">
                                <svg-icon class="w-24 h-24" type="business-center"></svg-icon>
                                <div>
                                    <p class="event-title font-semibold m-0">{{ calendarEvent.title }}</p>
                                    <p class="m-0">{{ getTimeString(calendarEvent) }}</p>
                                </div>
                            </div>
                            <div class="flex flex-col items-end">
                                <svg-icon :type="calendarEvent.attendees.length < 2 ? 'person' : 'groups'"
                                          class="w-18 h-18"></svg-icon>
                                <p class="m-0">
                                    {{
                                        calendarEvent.transparency === "opaque" ? 'busy' : (calendarEvent.transparency === "transparent" ? 'free' : '')
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div v-else class="flex items-center justify-center grow">
                        <loading></loading>
                    </div>
                </div>
            </div>
            <div
                class="transition-opacity ease-in-out duration-200 absolute top-0 left-0 bg-primary-80 bg-blend-multiply z-90 h-full w-full"
                :class="eventDetails ? 'opacity-30' : 'opacity-0 pointer-events-none'"
                @click="eventDeselected"
            ></div>
            <div
                class="2xl:transition-[height] transition-[width] duration-200 absolute right-0 bottom-0 z-100 bg-inherit shadow-3xl overflow-y-scroll rounded-6 hide-scrollbar"
                :class="eventDetails ? 'w-2/3 2xl:w-full h-full 2xl:h-2/3 p-32 ease-out' : 'w-0 2xl:w-full h-full 2xl:h-0 ease-in'">
                <div class="sticky right-0 top-0 z-top flex justify-end">
                    <button class="z-top bg-none border-0 cursor-pointer"
                            @click="eventDeselected">
                        <svg-icon
                            type="cancel"
                            class="text-primary-80 w-18 h-18"
                        />
                    </button>
                </div>
                <div v-if="eventDetails">
                    <div>
                        <h2 class="text-body font-semibold capitalize">{{ eventDetails.title }}</h2>
                        <p>{{ getDateString(eventDetails) }}</p>
                        <p>{{ getTimeString(eventDetails) }}</p>
                    </div>
                    <div class="border-b border-primary-20 my-12"></div>
                    <div>
                        <div v-if="eventDetails.description" class="mb-12">
                            <h3 class="text-body-sm font-semibold mb-8">
                                {{ capitalize($t('calendar.dashboard.description')) }}
                            </h3>
                            <p>{{ eventDetails.description }}</p>
                        </div>
                        <div>
                            <h3 class="text-body-sm font-semibold mb-8">
                                {{ capitalize($t('calendar.dashboard.attendees')) }}
                            </h3>
                            <div>
                                <p v-if="eventDetails.attendees.length === 0">{{
                                        capitalize($t('calendar.dashboard.no_attendees'))
                                    }}</p>
                                <div v-else class="flex justify-between items-start">
                                    <div v-if="showAllAttendees || eventDetails.attendees.length <= 3"
                                         class="flex flex-wrap gap-8 grow">
                                        <tag
                                            v-for="attendee in eventDetails.attendees"
                                            :key="attendee.email"
                                        >
                                            {{ attendee.name !== null ? attendee.name : attendee.email }}
                                        </tag>
                                    </div>
                                    <div v-else class="flex flex-wrap gap-8 grow">
                                        <tag
                                            v-for="index in 3"
                                            :key="eventDetails.attendees[index - 1].email"
                                        >
                                            {{ eventDetails.attendees[index - 1].name !== null ? eventDetails.attendees[index - 1].name : eventDetails.attendees[index - 1].email }}
                                        </tag>
                                        <tag
                                            interactive
                                            @click="showAllAttendees = true"
                                        >
                                            {{ '+' + (eventDetails.attendees.length - 3) }}
                                        </tag>
                                    </div>
                                    <button
                                        v-if="eventDetails.attendees.length > 3"
                                        class="cursor-pointer transition-transform ease-in-out duration-200"
                                        @click="showAllAttendees = !showAllAttendees">
                                        <svg-icon type="keyboard-arrow-down"
                                                  :class="showAllAttendees ? 'rotate-180' : ''"
                                                  width="24"
                                                  height="24"
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-if="eventDetails.location">
                            <h3 class="text-body">{{ capitalize($t('calendar.dashboard.location')) }}</h3>
                            <p>{{ eventDetails.location }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else-if="errorLoadingEvents">
            <h2 class="mt-64 text-heading-2">{{ capitalize($t('calendar.no_calendar.info')) }}</h2>
            <svg-icon class="wiggle"
                      size="small"
                      type="error"/>
        </template>
        <template v-else>
            <div class="flex items-center space-x-12 mx-auto">
                <svg-icon class="w-24 h-24 text-primary-80" type="calendar-today"></svg-icon>

                <h2 class="text-body font-semibold -mb-2">
                    {{ capitalize($t('calendar.personal')) }}
                </h2>
            </div>

            <feature :message="capitalize($t('calendar.coming_soon'))"
                     message-if-no-access
                     feature-key="calendar"
                     sub-key="dashboard"
                     class="block pb-24"
            >
                <p class="text-primary-80 text-wrap my-8 text-center">
                    {{ capitalize($t('calendar.no_access')) }}
                </p>

                <div class="flex justify-center my-16">
                    <simple-button
                        @click="() => $router.push({ name: 'user' })"
                        look="primary"
                    >
                        {{ capitalize($t('auth.manage_settings')) }}
                    </simple-button>
                </div>
            </feature>
        </template>
    </div>
</template>


<script>
import SvgIcon from '../../components/ui/SvgIcon.vue';
import Feature from '../../components/Feature';
import {DatePicker} from 'v-calendar';
import styles from '../../../../../../styles.config.js';
import Moment from "moment";
import Loading from "../../components/ui/Loading.vue";
import capitalize from "../../mixins/capitalize";
import events from "../../mixins/calendar/dashboard/events"
import Tag from "../../components/ui/Tag.vue";
import SimpleButton from "../../components/ui/SimpleButton.vue";
import {mapStores} from "pinia";
import {useMainStore} from "../../state/store";

import 'v-calendar/style.css';

export default {
    components: {
        SimpleButton,
        Tag,
        Loading,
        Feature,
        SvgIcon,
        'v-date-picker': DatePicker,
    },
    props: {
        reload: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        reload: function () {
            this.schedule = [];
            this.fetchEvents(this.activeDate, new Date(this.activeDate.getFullYear(), this.activeDate.getMonth() + 1, 2));
        },
    },
    mixins: [capitalize, events],
    data() {
        return {
            loading: false,
            hasCalendar: true,
            errorLoadingEvents: false,
            activeDate: new Date(),
            selectAttribute: {
                highlight: {
                    class: 'bg-primary-80',
                    contentClass: 'text-white',
                },
            },
            showTasks: true,
            eventDetails: null,
            today: new Date(),
            showAllAttendees: false,
            legend: {
                'events': {
                    color: 'bg-primary-80',
                    label: this.$t('calendar.dashboard.events'),
                },
                'task': {
                    color: 'bg-semantic-success',
                    label: this.$t('tasks.tasks'),
                },
            },
        };
    },
    methods: {
        changeDatePicker(event) {
            const date = event.date;
            if (date !== this.activeDate) this.activeDate = date;
            this.getAttributes();
        },
        changeMonth(event) {
            let newDate = new Date(event.year, event.month - 1);
            this.fetchEvents(new Date(newDate.getFullYear(), newDate.getMonth(), 2), new Date(newDate.getFullYear(), newDate.getMonth() + 1, 2));
            if (new Date().getMonth() === event.month - 1) {
                this.activeDate = new Date();
            } else {
                this.activeDate = new Date(event.year, event.month - 1, 1);
            }
        },
        getDateString(calendarEvent) {
            let dateString = new Moment(calendarEvent.start, '', this.locale).format('LLLL');
            let lastIndex = dateString.indexOf(new Moment(calendarEvent.start, '', this.locale).format('YYYY'));
            dateString = dateString.substring(0, lastIndex);
            return this.cleanEndOfString(dateString);
        },
        getTimeString(calendarEvent) {
            return calendarEvent.isAllDay ? this.$t('calendar.all_day') : new Moment(calendarEvent.start, '', this.locale).format('HH:mm') + ' - ' + new Moment(calendarEvent.end, '', this.locale).format('HH:mm');
        },
        cleanEndOfString(string) {
            while (string.endsWith(' ') || string.endsWith(',')) {
                string = string.substring(0, string.length - 1);
            }
            return string;
        },
        eventSelected(calendarEvent) {
            this.eventDetails = calendarEvent;
        },
        eventDeselected() {
            this.eventDetails = null;
            this.showAllAttendees = false;
        },
        getSelectedDays(allDay = false, tasks = false) {
            let activeDateString = this.parseDateString(this.activeDate, 'date');
            let activeDayString = this.parseDateString(this.activeDate);
            if (this.schedule[activeDateString] === undefined) return [];
            let events = [];
            if (this.schedule[activeDateString][activeDayString] === undefined) return [];
            this.schedule[activeDateString][activeDayString].forEach(day => {
                // if ((allDay ? day.isAllDay : !day.isAllDay) || (tasks ? day.isTask : !day.isTask)) events.push(day);
                if (allDay && day.isAllDay) events.push(day);
                if (tasks && day.isTask) events.push(day);
                if (!allDay && !tasks && !day.isAllDay && !day.isTask) events.push(day);
            })
            return events;
        },
        moment(date) {
            return new Moment(date, '', this.locale).format('D MMMM YYYY');
        },
    },
    computed: {
        ...mapStores(useMainStore),
        locale()  {
            return this.mainStore.locale;
        },
        currentUser() {
            return this.mainStore.currentUser;
        },
        styles() {
            return styles;
        },
        agendaLabel() {
            if (this.activeDate.toDateString() === new Date().toDateString()) {
                return this.capitalize(this.$t('calendar.dashboard.day_title_today'));
            } else {
                return this.capitalize(this.$t('calendar.dashboard.day_title_date', {date: new Moment(this.activeDate, '', this.locale).format('D MMMM')}));
            }
        },
        selectedDayEvents() {
            return this.getSelectedDays();
        },
        selectedDayAllDayEvents() {
            return this.getSelectedDays(true);
        },
        selectedDayTasks() {
            return this.getSelectedDays(false, true)
        },
        minPage() {
            let date = new Date();
            date = date.setDate(date.getDate() - 40);
            date = new Date(date);
            return {
                year: date.getFullYear(),
                month: date.getMonth() + 2,
            }
        },
        maxPage() {
            let date = new Date();
            date = date.setDate(date.getDate() + 200);
            date = new Date(date);
            return {
                year: date.getFullYear(),
                month: date.getMonth(),
            }
        }
    },
    beforeMount() {
        this.fetchEvents(this.activeDate, new Date(this.activeDate.getFullYear(), this.activeDate.getMonth() + 1, 2));
    },
    mounted() {
        this.hasCalendar = this.mainStore.currentUser?.primary_calendar_id !== null;
    }
};
</script>
<style>
.dashboard-calendar .vc-container {
    border: 0 !important;
    width: 100%;
}

.dashboard-calendar .vc-day-box-center-bottom > * {
    margin-top: 3rem !important;
}

.event-title {
    display: -webkit-box !important;
    -webkit-line-clamp: 1 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
}

.hide-scrollbar::-webkit-scrollbar {
    display: none;
}

.hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.vc-day {
    @apply p-8;
}
</style>
